<template>
    <v-dialog v-model="dialog.show" max-width="1100" scrollable persistent content-class="accreditation-dialog">
        <v-card class="ui-card accreditation-modal">
            <header class="ui-card__header accreditation-header">
                <div>
                    <h3 class="ui-section-title">Credenciamento</h3>
                    <p v-if="dataset.data.id" class="ui-section-description">Registro #{{ dataset.data.id }}<span v-if="meetingName"> · {{ meetingName }}</span></p>
                </div>
                <div class="ui-card__actions accreditation-header-actions">
                    <ui-badge v-if="dataset.data.id" :text="statusLabel" :color="statusColor" />
                    <v-btn icon small aria-label="Fechar credenciamento" :disabled="isBusy" @click="goBack"><v-icon small>mdi-close</v-icon></v-btn>
                </div>
            </header>
            <v-progress-linear v-if="dataset.loading" indeterminate color="primary" />
            <v-card-text class="accreditation-body">
                <v-skeleton-loader v-if="dataset.loading" type="article, list-item-two-line, article" />
                <v-alert v-else-if="loadError" type="error" text>
                    Não foi possível carregar o credenciamento.
                    <v-btn text small @click="load">Tentar novamente</v-btn>
                </v-alert>
                <template v-else-if="dataset.data.id">
                    <section class="participant-summary">
                        <div class="participant-avatar"><v-icon color="primary">mdi-account-outline</v-icon></div>
                        <div class="participant-identity">
                            <h3>{{ participant.name || 'Participante sem nome' }}</h3>
                            <div class="participant-contact"><span>{{ formatRegistration(participant.registration) }}</span><span>{{ participant.email || 'E-mail não informado' }}</span></div>
                        </div>
                        <ui-badge :text="dataset.data.has_qgc_credit ? 'Localizado no QGC' : 'Fora do QGC'" :tone="dataset.data.has_qgc_credit ? 'success' : 'warning'" />
                    </section>
                    <v-tabs v-model="activeTab" class="accreditation-tabs" show-arrows>
                        <v-tab>Participante</v-tab>
                        <v-tab>Documentos e análise <span class="ui-tab-count">{{ reviewedDocuments }}/{{ documentCount }}</span></v-tab>
                        <v-tab>Procuradores <span class="ui-tab-count">{{ dataset.authorizedProcurators.length }}</span></v-tab>
                    </v-tabs>
                    <v-form ref="form" v-model="dataForm.validForm">
                        <section v-show="activeTab === 0" class="accreditation-section">
                            <h3 class="ui-section-title">Participação na assembleia</h3>
                            <p class="ui-section-description section-help">Confira o vínculo com o caso e a modalidade de participação.</p>
                            <v-alert dense text :type="dataset.data.has_qgc_credit ? 'success' : 'warning'">
                                {{ dataset.data.has_qgc_credit ? 'Participante localizado no QGC deste caso. Pode ser classificado como credor.' : 'Participante sem crédito com direito a voto neste caso (fora do QGC ou só extraconcursal). O acesso deve permanecer como ouvinte, sem direito a voto.' }}
                            </v-alert>
                            <div v-if="dataset.data.interest_justification" class="mb-4">
                                <div class="ui-label font-weight-medium mb-1">Justificativa de interesse (ouvinte)</div>
                                <div class="text-body-2" style="white-space: pre-line;">{{ dataset.data.interest_justification }}</div>
                            </div>
                            <v-select dense outlined class="participation-select" label="Participação"
                                :items="dataset.participationTypes" item-text="name" item-value="id"
                                :disabled="Number(dataset.data.status) !== 0 || isBusy" v-model="dataset.data.participation_type" hide-details />
                            <div class="analysis-record">
                                <v-icon :color="statusColor">{{ ({ 0: 'mdi-clock-outline', 2: 'mdi-close-circle-outline', 3: 'mdi-file-document-refresh-outline' })[Number(dataset.data.status)] || 'mdi-check-decagram-outline' }}</v-icon>
                                <div><strong>{{ statusLabel }}</strong><p>{{ Number(dataset.data.status) === 0 ? 'Confira os documentos e conclua a análise na próxima aba.' : 'Análise concluída' }}<span v-if="dataset.data.admin && dataset.data.admin.name"> por {{ dataset.data.admin.name }}</span></p></div>
                            </div>
                            <!-- Pedido unico: a data da solicitacao e a original, mesmo depois de regularizado -->
                            <div class="request-timeline">
                                <div><span class="ui-label">Solicitado em</span><strong>{{ dataset.data.created_at | defaultDate('DD/MM/YYYY HH:mm') }}</strong></div>
                                <div v-if="dataset.data.regularization_requested_at"><span class="ui-label">Regularização solicitada em</span><strong>{{ dataset.data.regularization_requested_at | defaultDate('DD/MM/YYYY HH:mm') }}</strong></div>
                                <div v-if="dataset.data.regularized_at"><span class="ui-label">Complementado em</span><strong>{{ dataset.data.regularized_at | defaultDate('DD/MM/YYYY HH:mm') }}</strong></div>
                            </div>
                            <v-alert v-if="dataset.data.regularized_at && dataset.data.regularization_reason" dense text type="info" class="mb-4">
                                <div class="font-weight-medium mb-1">Pedido complementado pelo credor/procurador. O que havia sido solicitado:</div>
                                <div class="text-body-2" v-html="dataset.data.regularization_reason"></div>
                            </v-alert>
                            <v-btn outlined color="primary" @click="activeTab = 1">{{ Number(dataset.data.status) === 0 ? 'Analisar documentos' : 'Consultar análise' }}<v-icon right small>mdi-arrow-right</v-icon></v-btn>
                        </section>
                        <section v-show="activeTab === 1" class="accreditation-section">
                            <div class="section-heading"><div><h3 class="ui-section-title">Documentos do credenciamento</h3><p class="ui-section-description section-help">Abra os arquivos e registre a situação de cada documento.</p></div>
                                <ai-analysis-button v-if="dataset.data.has_procurator == 1" :endpoint="`/api/admin/ai/accreditations/${dataset.data.id}/procurator/analyze`" label="Analisar procuração com IA" title="Análise IA da procuração" />
                            </div>
                            <div class="accreditation-documents"><v-card-text v-if="dataset.data.association_documents">
                                            <v-alert dense text type="info" class="mb-0">
                                                <div class="font-weight-bold mb-1">Documentos da associação aprovada (procurador já vinculado ao credor)</div>
                                                <div class="d-flex flex-wrap align-center" style="gap: 8px;">
                                                    <v-btn v-if="dataset.data.association_documents.creditor_document_url" small outlined color="primary" :href="dataset.data.association_documents.creditor_document_url" target="_blank">
                                                        <v-icon left small>mdi-card-account-details-outline</v-icon> Documento do credor
                                                    </v-btn>
                                                    <v-btn v-if="dataset.data.association_documents.procuration_document_url" small outlined color="primary" :href="dataset.data.association_documents.procuration_document_url" target="_blank">
                                                        <v-icon left small>mdi-file-document-outline</v-icon> Procuração
                                                    </v-btn>
                                                </div>
                                                <div class="mt-2">
                                                    <strong>Poderes da procuração:</strong>
                                                    <span v-if="dataset.data.association_documents.procuration_powers && dataset.data.association_documents.procuration_powers.length">
                                                        {{ dataset.data.association_documents.procuration_powers.map(powerLabel).join(', ') }}
                                                    </span>
                                                    <span v-else class="ui-muted">não informados</span>
                                                </div>
                                                <div class="mt-1">
                                                    <ui-badge v-if="dataset.data.association_documents.has_agc_power" text="Com poder de participação em assembleia" tone="success" />
                                                    <ui-badge v-else text="Sem poder de participação em assembleia" tone="danger" />
                                                </div>
                                                <div v-if="!dataset.data.docs || !dataset.data.docs.length" class="text-caption mt-1">
                                                    Nenhum documento novo foi enviado neste credenciamento; a análise usa os documentos acima.
                                                </div>
                                            </v-alert>
                                        </v-card-text>

                                        <!-- Poderes informados para a procuracao anexada neste credenciamento -->
                                        <v-card-text v-if="dataset.data.has_procurator && !dataset.data.association_documents && dataset.data.procuration_powers">
                                            <div class="ui-label mb-1"><strong>Poderes da procuração anexada</strong> (informados pelo solicitante)</div>
                                            <div v-if="dataset.data.procuration_powers.length" class="d-flex flex-wrap" style="gap: 4px;">
                                                <ui-badge v-for="power in dataset.data.procuration_powers" :key="power" :text="powerLabel(power)"
                                                    :tone="power === 'agc_participation' ? 'success' : 'primary'" :dot="false" />
                                            </div>
                                            <span v-else class="ui-muted ui-small">Não informados.</span>
                                            <div v-if="!dataset.data.procuration_powers.includes('agc_participation')" class="ui-small error--text mt-1">
                                                O solicitante não indicou o poder de Participação em Assembleia de Credores. Confira na procuração.
                                            </div>
                                        </v-card-text>

                                        <v-card-text v-for="(item, index) in dataset.data.docs" :key="index">
                                            <!-- cor do status (vem do tipo de documento) so na borda esquerda -->
                                            <div class="action-container"
                                                :style="{ borderLeftColor: getColorByStatus(item.doc.type, item.doc.status) }">
                                                <span>
                                                    <span class="ui-muted">{{ index + 1 }}.</span>
                                                    <span class="ui-cell-main">{{ item.doc.name }}</span>
                                                    <span class="ui-muted">({{
                                                        item.doc.type | enum(dataset.documentTypes, 'id', 'name')
                                                        }})</span></span>

                                                <span class="ml-5">
                                                    <span class="ui-muted">Status: </span>
                                                    {{ filterStatusByDocumentType(item.doc.type, item.doc.status) }}
                                                </span>

                                                <v-spacer></v-spacer>

                                                <v-tooltip bottom>
                                                    <template #activator="{ on }">
                                                        <v-btn v-on="on" @click="openDoc(item.doc.fileUrl)" small outlined color="primary"><v-icon left small>mdi-eye-outline</v-icon>Abrir</v-btn>
                                                    </template>
                                                    <span>Visualizar documento</span>
                                                </v-tooltip>

                                                <v-menu bottom offset-y v-if="dataset.data.status == 0">
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-tooltip bottom>
                                                            <template
                                                                v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                                                                <v-btn small outlined color="primary" v-bind="attrs"
                                                                    v-on="Object.assign({}, on, tooltipOn)" class="ml-2">
                                                                    Avaliar
                                                                </v-btn>
                                                            </template>
                                                            <span>Informe se o documento é válido ou inválido</span>
                                                        </v-tooltip>
                                                    </template>

                                                    <v-list>
                                                        <v-list-item
                                                            v-for="(itemList, indexList) in filterStatusListByDocumentType(item.doc.type)"
                                                            :key="indexList" @click="item.doc.status = itemList.id">
                                                            <v-list-item-title>{{ itemList.name }}</v-list-item-title>
                                                        </v-list-item>
                                                    </v-list>
                                                </v-menu>

                                            </div>
                                        </v-card-text></div>
                            <v-alert v-if="!documentCount && !dataset.data.association_documents" dense text type="info">Nenhum documento anexado a este credenciamento.</v-alert>
                            <div class="analysis-decision">
                                <h3 class="ui-section-title">{{ Number(dataset.data.status) === 0 ? 'Concluir análise' : 'Análise registrada' }}</h3>
                                <v-select v-if="Number(dataset.data.status) === 0" class="mt-4" dense outlined hide-details label="Resultado da análise"
                                    :items="dataset.analysisStatusOptions" item-text="name" item-value="id" v-model="dataset.data.analysis_status" :disabled="isBusy" />
                                <v-alert v-if="Number(dataset.data.status) === 0 && Number(dataset.data.analysis_status) === 3" dense text type="info" class="mt-4 mb-0">
                                    O credor/procurador recebe o pedido por e-mail e complementa este mesmo credenciamento em "Regularizar". A data original da solicitação é mantida.
                                </v-alert>
                                <p class="ui-label mt-4 mb-2">{{ Number(dataset.data.analysis_status) === 3 && Number(dataset.data.status) === 0 ? 'O que deve ser regularizado' : 'Motivo / observações' }} <span v-if="isReasonRequired()" class="error--text">*</span></p>
                                <tiptap-vuetify :disabled="Number(dataset.data.status) !== 0 || isBusy" v-model="dataset.data.reason"
                                    :rules="isReasonRequired() ? [$rules.required] : []" :extensions="extensions" hide-details="auto" />
                                <v-btn v-if="Number(dataset.data.status) === 0" class="mt-2" text small color="primary" @click="openDialogDynamicText(true)"><v-icon left small>mdi-text-short</v-icon>Inserir texto predefinido</v-btn>
                            </div>
                        </section>
                        <section v-show="activeTab === 2" class="accreditation-section">
                            <h3 class="ui-section-title">Representação do participante</h3>
                            <p class="ui-section-description section-help">Consulte os procuradores autorizados e gerencie a representação deste credenciamento.</p>
                                                                        <v-row>
                                                <v-col cols="12" sm="4" v-if="dataset.data.has_procurator == 1">
                                                    <v-text-field dense id="procurador" label="Nome procurador"
                                                        :disabled="dataset.data.status !== 0"
                                                        v-model="dataset.data.procurator_name" outlined
                                                        hide-details="auto">
                                                    </v-text-field>
                                                </v-col>

                                                <v-col cols="12" sm="4" v-if="dataset.data.has_procurator == 1">
                                                    <v-text-field dense id="email_procurador" label="Email procurador"
                                                        :disabled="dataset.data.status !== 0"
                                                        v-model="dataset.data.procurator_email" outlined
                                                        hide-details="auto">
                                                    </v-text-field>
                                                </v-col>
                                                <v-col cols="12" sm="4" v-if="dataset.data.has_procurator == 1">
                                                    <v-text-field dense id="oab_procurador" label="OAB (se advogado)"
                                                        :disabled="dataset.data.status !== 0"
                                                        v-model="dataset.data.procurator_registration" outlined
                                                        :rules="dataset.data.procurator_oab_state ? [$rules.required] : []"
                                                        hide-details="auto">
                                                    </v-text-field>
                                                </v-col>
                                                <v-col cols="12" sm="4" v-if="dataset.data.has_procurator == 1">
                                                    <v-select dense id="uf_oab_procurador" label="UF da OAB"
                                                        :items="dataset.statesList" item-text="name" item-value="name"
                                                        :disabled="dataset.data.status !== 0"
                                                        v-model="dataset.data.procurator_oab_state" outlined
                                                        :rules="dataset.data.procurator_registration ? [$rules.required] : []"
                                                        hide-details="auto">
                                                    </v-select>
                                                </v-col>
                                            </v-row>


                                                                        <v-row v-if="dataset.data.accreditation && dataset.data.accreditation.user">
                                                <v-col cols="12">
                                                    <v-divider class="my-4"></v-divider>
                                                    <div class="d-flex align-center justify-space-between mb-3">
                                                        <div>
                                                            <div class="ui-section-title">Procuradores autorizados para este credor/caso</div>
                                                            <div class="ui-section-description">
                                                                Associações aprovadas vinculadas ao credor e ao caso selecionado.
                                                            </div>
                                                        </div>
                                                        <v-progress-circular
                                                            v-if="dataset.authorizedProcuratorsLoading"
                                                            indeterminate
                                                            color="primary"
                                                            size="22">
                                                        </v-progress-circular>
                                                    </div>

                                                    <v-alert
                                                        v-if="!dataset.authorizedProcuratorsLoading && !dataset.authorizedProcurators.length"
                                                        dense
                                                        text
                                                        type="info"
                                                        class="mb-0">
                                                        Nenhum procurador adicional aprovado para este credor neste caso.
                                                    </v-alert>

                                                    <v-simple-table
                                                        v-else-if="dataset.authorizedProcurators.length"
                                                        dense
                                                        class="authorized-procurators-table">
                                                        <template v-slot:default>
                                                            <thead>
                                                                <tr>
                                                                    <th>Procurador</th>
                                                                    <th>CPF/CNPJ</th>
                                                                    <th>E-mail</th>
                                                                    <th>OAB</th>
                                                                    <th>Origem</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr
                                                                    v-for="(procurator, index) in dataset.authorizedProcurators"
                                                                    :key="`authorized-procurator-${index}`">
                                                                    <td class="ui-cell-main">{{ procurator.name || 'N/A' }}</td>
                                                                    <td>{{ formatRegistration(procurator.registration) }}</td>
                                                                    <td>{{ procurator.email || 'N/A' }}</td>
                                                                    <td>{{ formatOab(procurator) }}</td>
                                                                    <td>
                                                                        <ui-badge :text="procurator.origin" tone="primary" />
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </template>
                                                    </v-simple-table>
                                                </v-col>
                                            </v-row>
                            <div class="substitution-section">
                                <div class="section-heading"><div><h3 class="ui-section-title">Substabelecimento</h3><p class="ui-section-description section-help">Substitua o procurador sem reabrir a análise.</p></div>
                                    <v-btn outlined color="primary" :disabled="isBusy" @click="showSubstitution = !showSubstitution" :aria-expanded="String(showSubstitution)">{{ showSubstitution ? 'Recolher formulário' : 'Substituir procurador' }}</v-btn>
                                </div>
                                <div v-if="showSubstitution" class="mt-4"><v-alert dense text type="info" class="mb-4">
	                                                Use esta opção para substituir o procurador do credenciamento sem reabrir a análise e sem depender do prazo da assembleia.
	                                            </v-alert>
	                                            <v-row>
	                                                <v-col cols="12" sm="6">
	                                                    <v-text-field dense label="Novo nome do procurador"
	                                                        v-model="dataset.procuratorSubstitution.procurator_name"
	                                                        :rules="[$rules.required]" outlined hide-details="auto">
	                                                    </v-text-field>
	                                                </v-col>
	                                                <v-col cols="12" sm="6">
	                                                    <v-text-field dense label="Novo e-mail do procurador"
	                                                        v-model="dataset.procuratorSubstitution.procurator_email"
	                                                        :rules="[$rules.required]" outlined hide-details="auto">
	                                                    </v-text-field>
	                                                </v-col>
		                                            </v-row>
		                                            <v-row>
		                                                <v-col cols="12" sm="6">
		                                                    <v-text-field dense label="OAB (se advogado)"
		                                                        v-model="dataset.procuratorSubstitution.procurator_registration"
		                                                        :rules="dataset.procuratorSubstitution.procurator_oab_state ? [$rules.required] : []" outlined hide-details="auto">
		                                                    </v-text-field>
		                                                </v-col>
		                                                <v-col cols="12" sm="6">
		                                                    <v-select dense label="UF da OAB"
		                                                        v-model="dataset.procuratorSubstitution.procurator_oab_state"
		                                                        :items="dataset.statesList" item-text="name" item-value="name"
		                                                        :rules="dataset.procuratorSubstitution.procurator_registration ? [$rules.required] : []" outlined hide-details="auto">
		                                                    </v-select>
		                                                </v-col>
		                                            </v-row>
		                                            <v-row>
		                                                <v-col cols="12">
		                                                    <v-checkbox dense hide-details
		                                                        v-model="dataset.procuratorSubstitution.use_same_procuration"
		                                                        label="Usar a mesma procuração">
		                                                    </v-checkbox>
		                                                </v-col>
		                                            </v-row>
		                                            <v-row v-if="!dataset.procuratorSubstitution.use_same_procuration">
		                                                <v-col cols="12">
		                                                    <v-file-input dense outlined show-size prepend-icon=""
		                                                        accept="image/png, image/jpeg, application/pdf"
		                                                        label="Nova procuração"
		                                                        v-model="dataset.procuratorSubstitution.procuration_file"
		                                                        hide-details="auto">
		                                                    </v-file-input>
		                                                </v-col>
		                                            </v-row>
		                                            <v-row>
		                                                <v-col cols="12">
	                                                    <v-btn v-if="$allowed('acc.substitute')" color="primary" depressed
	                                                        :loading="dataForm.substitutionLoading"
	                                                        @click="saveProcuratorSubstitution">
	                                                        <v-icon left small>mdi-account-switch-outline</v-icon>
	                                                        Salvar substabelecimento
	                                                    </v-btn>
	                                                </v-col>
	                                            </v-row>
	                                        </div>
                            </div>
                        </section>
                    </v-form>
                </template>
            </v-card-text>
            <footer class="ui-card__footer accreditation-footer">
                <span class="ui-small ui-muted" v-if="dataset.data.id && !dataset.loading">{{ Number(dataset.data.status) === 0 ? reviewedDocuments + ' de ' + documentCount + ' documentos analisados' : statusLabel }}</span>
                <div class="footer-actions">
                    <v-btn outlined :disabled="isBusy" @click="goBack">Fechar</v-btn>
                    <template v-if="dataset.data.id && !dataset.loading && !loadError">
                        <v-btn v-if="Number(dataset.data.status) === 0 && activeTab !== 1" color="primary" depressed @click="activeTab = 1">Ir para análise</v-btn>
                        <v-btn v-else-if="(Number(dataset.data.status) === 0) && $allowed('acc.decide')" color="primary" depressed :loading="dataForm.updateLoading" :disabled="isBusy && !dataForm.updateLoading" @click="actionGeneralMeeting">Salvar análise</v-btn>
                        <v-btn v-else-if="$allowed('acc.decide')" outlined color="primary" :loading="dataForm.restoreLoading" :disabled="isBusy && !dataForm.restoreLoading" @click="restoreAnalysis"><v-icon left small>mdi-restore</v-icon>Reabrir análise</v-btn>
                    </template>
                </div>
            </footer>
            <v-dialog v-model="dialogDynamicText" :width="900" scrollable>
                <v-card class="ui-card" :max-height="700">
                    <div class="ui-card__header">
                        <div>
                            <h3 class="ui-section-title">Textos Automáticos</h3>
                            <p class="ui-section-description">Selecione um texto abaixo para adicionar no motivo</p>
                        </div>
                    </div>
                    <v-card-text class="ui-card__body pt-0 px-5 pb-5">
                        <v-list-item two-line v-for="(item, index) in dataset.dynamicTexts" :key="index"
                            style="cursor: pointer" @click="selectDynamicText(item)">
                            <v-list-item-content>
                                <v-list-item-title class="font-weight-medium">{{ item.name
                                    }}</v-list-item-title>
                                <v-list-item-subtitle>
                                    <div
                                        v-html="item.description">
                                    </div>
                                    <v-divider></v-divider>
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-card-text>
                    <div class="ui-card__footer">
                        <v-btn type="button" outlined
                            @click="dialogDynamicText = false;">Cancelar</v-btn>
                    </div>
                </v-card>
            </v-dialog>
        </v-card>
    </v-dialog>
</template>
<script>
import procurationPowers from "@/common/data/procuration-powers.data";
import 'moment/locale/pt-br';

import axios from "axios"
import AIAnalysisButton from "@/components/AIAnalysisButton.vue"

import documentTypes from "@/common/data/document-types.data"
import statesList from "@/common/data/states-list.data"

import {
    TiptapVuetify, Heading, Bold, Italic, Strike, Underline, Code, Paragraph, BulletList, OrderedList, ListItem, Link, Blockquote, HardBreak, HorizontalRule, History, Table, TableCell, TableHeader,
    TableRow, TodoList, TodoItem
} from 'tiptap-vuetify'

export default {
    data: () => ({
        activeTab: 0,
        showSubstitution: false,
        loadError: false,
        dialogDynamicText: false,
        dynamicTextSmallType: false,

        dataset: {
            data: {
                reason: '',
            },
	            dynamicTexts: [],
	            accreditation: null,
		            loading: false,
		            documentTypes: documentTypes,
		            statesList,
		            procuratorSubstitution: {
		                procurator_name: '',
		                procurator_email: '',
		                procurator_registration: '',
		                procurator_oab_state: '',
		                use_same_procuration: true,
		                procuration_file: null,
		            },
                authorizedProcurators: [],
                authorizedProcuratorsLoading: false,
	            analysisStatusOptions: [
	                { id: 1, name: 'Aprovar' },
	                { id: 2, name: 'Rejeitar' },
	                { id: 3, name: 'Solicitar regularização' },
	            ],
	                participationTypes: [
	                    { id: 1, name: 'Credor' },
	                    { id: 2, name: 'Ouvinte' },
                ]
	        },

        dialog: {
            show: false,
            resolve: null,
            reject: null,
        },

        dataForm: {
	            validForm: true,
	            addLoading: false,
	            updateLoading: false,
	            removeLoading: false,
	            substitutionLoading: false,
	            restoreLoading: false,
	        },

        extensions: [
            History,
            Blockquote,
            Link,
            Underline,
            Strike,
            Italic,
            ListItem,
            BulletList,
            OrderedList,
            [Heading, {
                options: {
                    levels: [1, 2, 3]
                }
            }],
            Bold,
            Code,
            HorizontalRule,
            Paragraph,
            HardBreak,
            [Table, {
                options: {
                    resizable: true
                }
            }],
            TableCell,
            TableHeader,
            TableRow,
        ],
    }),

    components: { 'tiptap-vuetify': TiptapVuetify, 'ai-analysis-button': AIAnalysisButton },

    mounted: function () {
    },

    computed: {
        participant() { return (this.dataset.data.accreditation || {}).user || {}; },
        meetingName() { return (this.dataset.data.general_meeting || this.dataset.data.generalMeeting || {}).name || ''; },
        statusLabel() { return ({ 0: 'Pendente de análise', 1: 'Aprovado', 2: 'Rejeitado', 3: 'Regularização solicitada' })[Number(this.dataset.data.status)] || 'Não informado'; },
        statusColor() { return ({ 0: 'warning', 1: 'success', 2: 'error', 3: 'purple' })[Number(this.dataset.data.status)] || 'grey'; },
        documentCount() { return (this.dataset.data.docs || []).length; },
        reviewedDocuments() { return (this.dataset.data.docs || []).filter(item => Number(item.doc.status) !== 0).length; },
        isBusy() { return this.dataForm.updateLoading || this.dataForm.restoreLoading || this.dataForm.substitutionLoading; }
    },

    methods: {
        powerLabel(id) {
            const found = procurationPowers.find((item) => item.id === id);
            return found ? found.name : id;
        },


        open() {
            this.dataset.data = {};
            this.activeTab = 0;
            this.showSubstitution = false;
            this.dialog.show = true;

            this.load()

            return new Promise((resolve, reject) => {
                this.dialog.resolve = resolve;
                this.dialog.reject = reject;
            });
        },

        close() {
            if (this.dialog.resolve) this.dialog.resolve()
            this.dialog.show = false;
        },

        setData(data) {
            this.dataset.accreditation = data;
        },

        goBack() {
            this.dataset.data = {};
            this.dataset.accreditation = null;
            this.close();
        },

        openDoc(doc) {
            window.open(doc, '_blank');
        },

        openDialogDynamicText(small = false) {
            this.loadDynamicTexts()

            this.dynamicTextSmallType = small;
            this.dialogDynamicText = true;

        },

        getFinalStatus() {

            if (!this.dataset.data.docs) return { id: -1, text: 'pendente' }

            let result = { id: 1, text: 'aprovado' };

            for (let item of this.dataset.data.docs) {
                if (item.doc.status === 0) return { id: -1, text: 'pendente' };

                const documentType = this.dataset.documentTypes.find((x) => x.id === item.doc.type)

                const resultStatus = documentType.status.find((x) => x.id === item.doc.status);

                if (!resultStatus.approved) return { id: 2, text: 'rejeitado' };

            }

            return result;

        },

        getColorByStatus(type, status) {
            if (status === 0) return '#FFEB3B'

            const documentType = this.dataset.documentTypes.find((x) => x.id === type)

            if (!documentType) return '#FFEB3B'

            const resultStatus = documentType.status.find((x) => x.id === status);

            if (!resultStatus) return '#FB3A04'

            return resultStatus.color
        },

        filterStatusByDocumentType(type, status) {
            const documentType = this.dataset.documentTypes.find((x) => x.id === type)

            if (!documentType) return 'Tipo de documento não encontrado'

            const resultStatus = documentType.status.find((x) => x.id === status);

            if (!resultStatus) return 'Pendente'

            return resultStatus.name
        },

        filterStatusListByDocumentType(type) {
            const documentType = this.dataset.documentTypes.find((x) => x.id === type)

            if (!documentType) return []

            return documentType.status
        },

        selectDynamicText(item) {
            this.dataset.data.reason += item.small_description

            this.dialogDynamicText = false;
        },

        async load() {
            this.dataset.loading = true;
            this.loadError = false;

            try {
                const result = await axios.get(`/api/admin/accreditations/find/${this.dataset.accreditation.id}`);
                const data = result.data.content;

	                this.dataset.data = data;
                    this.dataset.data.status = Number(data.status);
	
	                this.dataset.data.reason = data.reason || ""
	                this.dataset.data.participation_type = Number(data.participation_type || 1)
	                this.dataset.data.analysis_status = data.status && data.status !== 0 ? Number(data.status) : 1
                    await this.loadAuthorizedProcurators()
		                this.dataset.procuratorSubstitution = {
		                    procurator_name: '',
		                    procurator_email: '',
		                    procurator_registration: '',
		                    procurator_oab_state: '',
		                    use_same_procuration: true,
		                    procuration_file: null,
		                }

	            } catch (error) {
                this.loadError = true;
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }

            this.dataset.loading = false;
        },

        async loadAuthorizedProcurators() {
            this.dataset.authorizedProcurators = [];

            const clientId = this.getAccreditationClientId();
            const creditorRegistration = this.getAccreditationCreditorRegistration();

            if (!clientId || !creditorRegistration) return;

            this.dataset.authorizedProcuratorsLoading = true;

            try {
                const result = await axios.get('/api/admin/procurator-associations', {
                    params: {
                        page: 1,
                        limit: 100,
                        search: {
                            client_id: clientId,
                            creditor_registration: creditorRegistration,
                            status: 1,
                        },
                    },
                });

                const associations = result.data.content.data || [];
                this.dataset.authorizedProcurators = this.normalizeAuthorizedProcurators(associations);
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            } finally {
                this.dataset.authorizedProcuratorsLoading = false;
            }
        },

        getAccreditationClientId() {
            if (!this.dataset.data) return null;
            if (this.dataset.data.general_meeting && this.dataset.data.general_meeting.client_id) return this.dataset.data.general_meeting.client_id;
            if (this.dataset.data.generalMeeting && this.dataset.data.generalMeeting.client_id) return this.dataset.data.generalMeeting.client_id;
            if (this.dataset.data.general_meeting && this.dataset.data.general_meeting.client && this.dataset.data.general_meeting.client.id) {
                return this.dataset.data.general_meeting.client.id;
            }
            if (this.dataset.data.accreditation && this.dataset.data.accreditation.client_id) return this.dataset.data.accreditation.client_id;
            return null;
        },

        getAccreditationCreditorRegistration() {
            if (!this.dataset.data || !this.dataset.data.accreditation || !this.dataset.data.accreditation.user) return null;
            return this.onlyDigits(this.dataset.data.accreditation.user.registration);
        },

        normalizeAuthorizedProcurators(associations) {
            const procurators = [];
            const keys = new Set();

            associations.forEach((association) => {
                if (association.procurator) {
                    this.pushAuthorizedProcurator(procurators, keys, {
                        name: association.procurator.name,
                        registration: association.procurator.registration,
                        email: association.procurator.email,
                        oab: association.procurator.oab,
                        oab_state: association.procurator.oab_state,
                        origin: 'Associação aprovada',
                    });
                }

                this.parseAuthorizedProcurators(association.authorized_procurators).forEach((procurator) => {
                    if (procurator.approved === false) return;

                    this.pushAuthorizedProcurator(procurators, keys, {
                        name: procurator.name,
                        registration: procurator.registration,
                        email: procurator.email,
                        oab: procurator.oab,
                        oab_state: procurator.oab_state,
                        origin: procurator.linked_association_id ? 'Procuração compartilhada' : 'Informado na procuração',
                    });
                });
            });

            return procurators;
        },

        pushAuthorizedProcurator(procurators, keys, procurator) {
            const key = this.onlyDigits(procurator.registration) || String(procurator.email || procurator.name || '').toLowerCase();
            if (!key || keys.has(key)) return;

            keys.add(key);
            procurators.push(procurator);
        },

        parseAuthorizedProcurators(value) {
            if (!value) return [];
            if (Array.isArray(value)) return value;

            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : [];
            } catch (error) {
                return [];
            }
        },

        onlyDigits(value) {
            return String(value || '').replace(/\D/g, '');
        },

        formatRegistration(value) {
            const digits = this.onlyDigits(value);
            if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
            if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
            return value || 'N/A';
        },

        formatOab(procurator) {
            if (!procurator || !procurator.oab) return 'N/A';
            return `${procurator.oab}${procurator.oab_state ? `/${procurator.oab_state}` : ''}`;
        },


        async loadDynamicTexts() {

            try {
                const result = await axios.get(`/api/admin/dynamic-texts`, { params: { start: 1, limit: 100000, search: {} } });

                this.dataset.dynamicTexts = result.data.content.data;

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }
        },

	        async actionGeneralMeeting() {

	            const documentStatus = this.getFinalStatus();
	            const finalStatus = this.getSelectedAnalysisStatus();
	
	            if (documentStatus.id === -1) {
	                this.$dialog.notify.error('É necessário verificar todos os documentos do credenciamento para continuar!', {
                    position: 'top-right',
                    timeout: 2000
                })

                return;
            }

            if (finalStatus.id === 1 && documentStatus.id === 2) {
                this.$dialog.notify.error('Não é possível aprovar credenciamento com documento inválido.', {
                    position: 'top-right',
                    timeout: 2000
                })

                return;
            }

            if (this.isReasonRequired() && !this.dataset.data.reason) {
                this.$dialog.notify.error(finalStatus.id === 3 ? 'Informe o que deve ser regularizado!' : 'Para rejeitar, informe um motivo!', {
                    position: 'top-right',
                    timeout: 2000
                })

                return;
            }

            const proceedAction = await this.$dialog.confirm({
                text: `Deseja salvar o credenciamento como ${finalStatus.text.toUpperCase()}?`,
                title: 'Atenção!',
                actions: {
                    false: 'Cancelar',
                    true: 'Confirmar'
                }
            })

            if (!proceedAction) return;

            this.dataForm.updateLoading = true;
            try {

                let payload = this.buildPayload();

                payload.status = finalStatus.id;

                const response = await axios.patch(`/api/admin/accreditations/action/${this.dataset.data.id}`, payload);

                this.$dialog.notify.info("Credenciamento atualizado com sucesso", { position: "top-right", timeout: 2000 });

                this.goBack()

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
	            } finally {
	                this.dataForm.updateLoading = false;
	            }
		        },

            async restoreAnalysis() {
                const proceedAction = await this.$dialog.confirm({
                    text: 'Deseja restaurar esta análise? O credenciamento voltará para PENDENTE e poderá ser analisado novamente.',
                    title: 'Restaurar análise',
                    actions: {
                        false: 'Cancelar',
                        true: 'Restaurar'
                    }
                })

                if (!proceedAction) return;

                this.dataForm.restoreLoading = true;

                try {
                    await axios.patch(`/api/admin/accreditations/${this.dataset.data.id}/restore-analysis`);
                    this.$dialog.notify.info("Análise restaurada com sucesso", { position: "top-right", timeout: 2000 });
                    await this.load();
                } catch (error) {
                    this.$dialog.notify.error(this.$codes(error), {
                        position: 'top-right',
                        timeout: 2000
                    })
                } finally {
                    this.dataForm.restoreLoading = false;
                }
            },

		        async saveProcuratorSubstitution() {
	            if (!this.dataset.procuratorSubstitution.procurator_name) {
	                this.$dialog.notify.error('Informe o nome do novo procurador.', {
	                    position: 'top-right',
	                    timeout: 2000
	                })
	                return;
	            }

		            if (!this.dataset.procuratorSubstitution.procurator_email) {
	                this.$dialog.notify.error('Informe o e-mail do novo procurador.', {
	                    position: 'top-right',
	                    timeout: 2000
	                })
		                return;
		            }

		            if (!!this.dataset.procuratorSubstitution.procurator_registration !== !!this.dataset.procuratorSubstitution.procurator_oab_state) {
		                this.$dialog.notify.error('Ao informar a OAB, preencha também a respectiva UF.', {
		                    position: 'top-right',
		                    timeout: 2000
		                })
		                return;
		            }

		            if (!this.dataset.procuratorSubstitution.use_same_procuration && !this.dataset.procuratorSubstitution.procuration_file) {
		                this.$dialog.notify.error('Informe a nova procuração.', {
		                    position: 'top-right',
		                    timeout: 2000
		                })
		                return;
		            }

	            const proceedAction = await this.$dialog.confirm({
	                text: 'Deseja substituir o procurador deste credenciamento?',
	                title: 'Substabelecimento de procurador',
	                actions: {
	                    false: 'Cancelar',
	                    true: 'Confirmar'
	                }
	            })

	            if (!proceedAction) return;

	            this.dataForm.substitutionLoading = true;

	            try {
		                const payload = new FormData();
		                payload.append('procurator_name', this.dataset.procuratorSubstitution.procurator_name);
		                payload.append('procurator_email', this.dataset.procuratorSubstitution.procurator_email);
		                payload.append('procurator_registration', this.dataset.procuratorSubstitution.procurator_registration || '');
		                payload.append('procurator_oab_state', this.dataset.procuratorSubstitution.procurator_oab_state || '');
		                payload.append('use_same_procuration', this.dataset.procuratorSubstitution.use_same_procuration ? '1' : '0');

		                if (!this.dataset.procuratorSubstitution.use_same_procuration) {
		                    payload.append('procuration_file', this.dataset.procuratorSubstitution.procuration_file);
		                }

		                await axios.patch(`/api/admin/accreditations/${this.dataset.data.id}/procurator-substitution`, payload);

	                this.$dialog.notify.info("Procurador substituído com sucesso", { position: "top-right", timeout: 2000 });
	                await this.load();
	            } catch (error) {
	                this.$dialog.notify.error(this.$codes(error), {
	                    position: 'top-right',
	                    timeout: 2000
	                })
	            } finally {
	                this.dataForm.substitutionLoading = false;
	            }
	        },

	        buildPayload() {
	            const data = {
	                procurator_email: this.dataset.data.procurator_email,
	                procurator_name: this.dataset.data.procurator_name,
	                procurator_registration: this.dataset.data.procurator_registration,
	                procurator_oab_state: this.dataset.data.procurator_oab_state,
	                participation_type: this.dataset.data.participation_type || 1,
                reason: this.dataset.data.reason,
                docs: this.dataset.data.docs.map((x) => { return { id: x.doc.id, status: x.doc.status } })
            }

	            return data;
	        },

            isReasonRequired() {
                return [2, 3].includes(this.getSelectedAnalysisStatus().id);
            },

            getSelectedAnalysisStatus() {
                const status = Number(this.dataset.data.analysis_status || 1);
                return {
                    id: status,
                    text: ({ 2: 'rejeitado', 3: 'regularização solicitada' })[status] || 'aprovado'
                };
            }
	    }
	};
</script>

<style scoped lang="scss">
@import "../assets/scss/ui/tokens";

.accreditation-modal { max-height: 90vh; display: flex; flex-direction: column; color: $ui-text; }
.accreditation-header { border-bottom:1px solid $ui-border-soft; flex-shrink:0; }
.accreditation-header-actions { flex-wrap: nowrap; }
.accreditation-body { padding:20px !important; overflow-y:auto; min-height:0; }
.participant-summary { display:flex; gap:16px; align-items:center; background:$ui-bg-muted; border:1px solid $ui-border; padding:16px; border-radius:$ui-radius-sm; margin-bottom:20px; }
.participant-avatar { background:$ui-accent-soft; padding:10px; border-radius:$ui-radius-sm; }
.participant-identity { flex:1; min-width:0; }
.participant-identity h3 { font-size:16px; font-weight:600; color:$ui-fg; overflow-wrap:anywhere; margin:0 0 4px; }
.participant-contact { display:flex; flex-wrap:wrap; gap:4px 20px; color:$ui-muted; font-size:13px; overflow-wrap:anywhere; }
.accreditation-tabs { border-bottom:1px solid $ui-border; margin-bottom:20px; }
.accreditation-tabs ::v-deep .v-tab { text-transform:none; letter-spacing:0; }
.section-help { margin-bottom:18px; }
.participation-select { max-width:340px; margin:20px 0; }
.request-timeline { display:flex; flex-wrap:wrap; gap:8px 32px; padding:14px 0; border-top:1px solid $ui-border-soft; }
.request-timeline > div { display:flex; flex-direction:column; gap:2px; }
.request-timeline strong { font-size:14px; font-weight:600; color:$ui-fg; }
.analysis-record { display:flex; align-items:flex-start; gap:12px; padding:18px 0; border-top:1px solid $ui-border-soft; }
.analysis-record p { margin:4px 0; font-size:13px; color:$ui-muted; }
.section-heading { display:flex; gap:16px; justify-content:space-between; align-items:center; flex-wrap:wrap; }
.accreditation-documents > .v-card__text { padding:12px 0; }
.action-container { display:flex; flex-wrap:wrap; align-items:center; gap:12px; border:1px solid $ui-border; border-left-width:3px; padding:14px 16px; border-radius:$ui-radius-sm; }
.action-container > span:first-child { flex:1 1 220px; overflow-wrap:anywhere; }
.action-container > span.ml-5 { margin-left:0 !important; font-size:12px; }
.analysis-decision, .substitution-section { border-top:1px solid $ui-border-soft; margin-top:24px; padding-top:22px; }
.authorized-procurators-table { border:1px solid $ui-border; border-radius:$ui-radius-sm; overflow:hidden; }
.accreditation-footer { align-items:center; justify-content:space-between; flex-shrink:0; }
.footer-actions { display:flex; flex-wrap:wrap; gap:8px; }
@media(max-width:600px) {
 .accreditation-header, .accreditation-body, .accreditation-footer { padding:16px !important; }
 .accreditation-header { flex-wrap:wrap; gap:12px; }
 .accreditation-header-actions { width:100%; justify-content:space-between; }
 .accreditation-section ::v-deep .v-btn { max-width:100%; height:auto; min-height:36px; padding:8px 12px; }
 .accreditation-section ::v-deep .v-btn__content { white-space:normal; }
 .accreditation-header-actions { gap:4px; }
 .participant-summary { flex-wrap:wrap; padding:14px; }
 .participant-avatar { display:none; }
 .participant-identity { flex-basis:100%; }
 .accreditation-footer { flex-wrap:wrap; }
 .footer-actions { width:100%; justify-content:flex-end; }
}
</style>
